import type { Language } from '../i18n/types';

export type HairLength = 'short' | 'medium' | 'long';
export type HairVibe = 'classic' | 'modern' | 'messy' | 'disconnected';

export type LocalizedValue<T> = Record<Language, T>;

export interface HairstyleConfig {
  id: string;
  name: LocalizedValue<string>;
  description: LocalizedValue<string>;
  tags: LocalizedValue<string[]>;
  variations: LocalizedValue<string[]>;
  promptHint: string;
  previewImage?: string;
  length?: HairLength;
  vibe?: HairVibe;
  hiddenInCatalog?: boolean;
}

export interface LocalizedHairstyleCopy {
  name: string;
  description: string;
  tags: string[];
  variations: string[];
}

const localized = <T>(ru: T, en: T, az: T): LocalizedValue<T> => ({ ru, en, az });

export const HAIRSTYLE_CONFIGS: HairstyleConfig[] = [
  {
    id: 'quiff',
    name: localized('Квифф', 'Quiff', 'Kviff'),
    description: localized('Объёмная укладка вверх и вперёд с аккуратными короткими висками.', 'Volume swept up and forward with clean, shorter sides.', 'Qısa yanlar və yuxarı-önə yönəlmiş həcmli, dinamik saç düzümü.'),
    tags: localized(['Объёмная', 'Динамичная'], ['Voluminous', 'Dynamic'], ['Həcmli', 'Dinamik']),
    variations: localized(['Небрежный', 'Текстурный', 'Аккуратный'], ['Messy', 'Textured', 'Neat'], ['Dağınıq', 'Teksturalı', 'Səliqəli']),
    promptHint: 'Quiff hairstyle, volume forward and up, short faded sides, modern barbershop cut',
    previewImage: '/styles/catalog/quiff.png', length: 'medium', vibe: 'modern',
  },
  {
    id: 'flow',
    name: localized('Флоу', 'Flow Cut', 'Flow Cut'),
    description: localized('Волосы средней длины с естественным направлением назад и мягким объёмом.', 'Medium-length hair flowing naturally back for an effortless athletic look.', 'Arxaya təbii axan orta uzunluqlu saçlar — rahat, idman üslubu.'),
    tags: localized(['Естественная', 'Спортивная'], ['Natural', 'Sporty'], ['Təbii', 'İdman']),
    variations: localized(['Классический', 'Волнистый', 'Слоистый'], ['Classic', 'Wavy', 'Layered'], ['Klassik', 'Dalğalı', 'Laylı']),
    promptHint: 'Flow haircut, medium length swept back, hockey hair, natural movement',
    previewImage: '/styles/catalog/flow.png', length: 'medium', vibe: 'modern',
  },
  {
    id: 'fringe',
    name: localized('Френдж-кат', 'Fringe Cut', 'Fringe Cut'),
    description: localized('Текстурная чёлка, уложенная вперёд, для современного выразительного силуэта.', 'A textured fringe worn forward for a modern, defined silhouette.', 'Müasir və dəqiq siluet üçün önə doğru teksturalı çolka.'),
    tags: localized(['Текстурная', 'Современная'], ['Textured', 'Modern'], ['Teksturalı', 'Müasir']),
    variations: localized(['Короткая', 'Мягкая', 'Текстурная'], ['Short', 'Soft', 'Textured'], ['Qısa', 'Yumşaq', 'Teksturalı']),
    promptHint: 'Fringe cut, textured bangs, modern fringe hairstyle',
    previewImage: '/styles/catalog/fringe.png', length: 'medium', vibe: 'modern',
  },
  {
    id: 'gent',
    name: localized('Гент', 'Gent', 'Gent'),
    description: localized('Классическая аккуратная стрижка с чистой формой и сдержанным финишем.', 'A timeless, polished cut with a refined shape and clean finish.', 'Səliqəli forma və təmiz tamamlanma ilə zamansız klassik saç düzümü.'),
    tags: localized(['Классика', 'Элегантная'], ['Classic', 'Elegant'], ['Klassik', 'Zərif']),
    variations: localized(['С пробором', 'Естественный', 'Гладкий'], ['Side part', 'Natural', 'Sleek'], ['Yan ayrım', 'Təbii', 'Parlaq']),
    promptHint: 'Gentleman haircut, refined style, classic elegant cut',
    previewImage: '/styles/catalog/gent.png', length: 'medium', vibe: 'classic',
  },
  {
    id: 'middle_part',
    name: localized('Мидл парт', 'Middle Part', 'Orta ayrım'),
    description: localized('Пробор по центру с естественным направлением волос на обе стороны.', 'A centred part with hair falling naturally to each side.', 'Saçların hər iki yana təbii düşdüyü ortadan ayrım.'),
    tags: localized(['Трендовая', 'Средняя длина'], ['Trend-led', 'Medium length'], ['Trend', 'Orta uzunluq']),
    variations: localized(['Мягкий', 'Шторки', 'Гладкий'], ['Soft', 'Curtain', 'Sleek'], ['Yumşaq', 'Pərdə', 'Səliqəli']),
    promptHint: 'Middle part hairstyle, center parting, modern styling',
    previewImage: '/styles/catalog/middle_part.png', length: 'medium', vibe: 'modern',
  },
  {
    id: 'mod',
    name: localized('Мод кат', 'Mod Cut', 'Mod Cut'),
    description: localized('Чёткая современная форма для уверенного образа, который следует трендам.', 'A sharp contemporary cut for a confident, trend-aware look.', 'Özünəinamlı və trendi izləyənlər üçün dəqiq, müasir saç düzümü.'),
    tags: localized(['Современная', 'Смелая'], ['Modern', 'Bold'], ['Müasir', 'Cəsarətli']),
    variations: localized(['Короткий', 'Текстурный', 'Чистый'], ['Short', 'Textured', 'Clean'], ['Qısa', 'Teksturalı', 'Təmiz']),
    promptHint: 'Mod cut, contemporary style, modern fashionable haircut',
    previewImage: '/styles/catalog/mod.png', length: 'medium', vibe: 'modern',
  },
  {
    id: 'oxford',
    name: localized('Оксфорд', 'Oxford', 'Oxford'),
    description: localized('Сдержанная британская классика: точная форма, аккуратность и элегантность.', 'A classic British-inspired shape: understated, precise, and elegant.', 'Sakit, dəqiq və zərif klassik Britaniya üslubu.'),
    tags: localized(['Британская классика', 'Аккуратная'], ['British classic', 'Neat'], ['Britaniya klassikası', 'Səliqəli']),
    variations: localized(['Классический', 'Плавный переход', 'С пробором'], ['Classic', 'Tapered', 'Side part'], ['Klassik', 'Keçidli', 'Yan ayrım']),
    promptHint: 'Oxford cut, traditional english style, classic british haircut',
    previewImage: '/styles/catalog/oxford.png', length: 'medium', vibe: 'classic',
  },
  {
    id: 'warrior',
    name: localized('Варриор кат', 'Warrior Cut', 'Warrior Cut'),
    description: localized('Смелая мужская стрижка с сильной формой и выраженным характером.', 'A bold, masculine cut with strong shape and unmistakable character.', 'Güclü forma və ifadəli xarakteri olan cəsarətli kişi saç düzümü.'),
    tags: localized(['Смелая', 'Мужественная'], ['Bold', 'Masculine'], ['Cəsarətli', 'Kişiyəxas']),
    variations: localized(['Текстурный', 'Короткий', 'Выраженный'], ['Textured', 'Short', 'Defined'], ['Teksturalı', 'Qısa', 'Dəqiq']),
    promptHint: 'Warrior cut, bold masculine style, daring haircut',
    previewImage: '/styles/catalog/warrior.png', length: 'short', vibe: 'modern',
  },
  {
    id: 'mullet',
    name: localized('Маллет', 'Mullet', 'Mullet'),
    description: localized('Короткие верх и виски с удлинённой затылочной частью — смелый современный образ.', 'Shorter top and sides with length at the back — an expressive modern revival.', 'Yuxarı və yanlar qısa, arxa hissə uzundur — müasir və ifadəli seçim.'),
    tags: localized(['Смелая', 'Творческая'], ['Bold', 'Creative'], ['Cəsarətli', 'Yaradıcı']),
    variations: localized(['Мягкий', 'Современный', 'Текстурный'], ['Soft', 'Modern', 'Textured'], ['Yumşaq', 'Müasir', 'Teksturalı']),
    promptHint: 'Modern mullet haircut, short on top and sides, longer in back, business in front party in back',
    previewImage: '/styles/catalog/mullet.jpg', length: 'long', vibe: 'modern',
  },
  {
    id: 'caesar',
    name: localized('Цезарь', 'Caesar', 'Caesar'),
    description: localized('Короткая стрижка с ровной чёлкой, не требующая сложной укладки.', 'A low-maintenance short cut with a clean horizontal fringe.', 'Düz çolkalı, mürəkkəb formalaşdırma istəməyən qısa saç düzümü.'),
    tags: localized(['Минимализм', 'Практичная'], ['Minimal', 'Practical'], ['Minimal', 'Rahat']),
    variations: localized(['Классический', 'Текстурный', 'Короткий фейд'], ['Classic', 'Textured', 'Short fade'], ['Klassik', 'Teksturalı', 'Qısa fade']),
    promptHint: 'Caesar cut, short horizontal fringe, uniform length, clean classic look',
    previewImage: '/styles/catalog/caesar.jpg', length: 'short', vibe: 'classic',
  },
  {
    id: 'crop',
    name: localized('Кроп', 'Textured Crop', 'Teksturalı Crop'),
    description: localized('Текстурный верх с прямой или рваной чёлкой — практичный стиль на каждый день.', 'Textured top with a straight or broken fringe: practical and stylish.', 'Düz və ya qırıq çolka ilə teksturalı üst: gündəlik üçün praktik seçim.'),
    tags: localized(['Текстурная', 'На каждый день'], ['Textured', 'Everyday'], ['Teksturalı', 'Gündəlik']),
    variations: localized(['Французский кроп', 'Небрежный', 'Скин-фейд'], ['French crop', 'Messy', 'Skin fade'], ['French crop', 'Dağınıq', 'Skin fade']),
    promptHint: 'Short french crop with blunt fringe and faded sides, textured top, contemporary barbershop style',
    previewImage: '/styles/catalog/crop.jpg', length: 'short', vibe: 'modern',
  },
  {
    id: 'curly_volume',
    name: localized('Кудрявый объём', 'Curly Volume', 'Buruq həcm'),
    description: localized('Форма, которая подчёркивает естественные кудри, объём и текстуру волос.', 'A shape that brings out natural curls, volume, and texture.', 'Təbii buruqları, həcmi və saçın teksturasını önə çıxaran forma.'),
    tags: localized(['Кудрявая', 'Объёмная'], ['Curly', 'Voluminous'], ['Buruq', 'Həcmli']),
    variations: localized(['Естественный', 'Выраженный', 'Плавный переход'], ['Natural', 'Defined', 'Tapered'], ['Təbii', 'Dəqiq', 'Keçidli']),
    promptHint: 'Medium-length curly hair with natural volume, defined curls (not afro-like), barbershop styling',
    previewImage: '/styles/catalog/curly_volume.webp', length: 'medium', vibe: 'modern',
  },
  {
    id: 'buzz',
    name: localized('Базз кат', 'Buzz Cut', 'Buzz Cut'),
    description: localized('Чистая короткая стрижка машинкой — практичный, уверенный и универсальный выбор.', 'A clean clipper cut for a practical, confident, universal look.', 'Praktik, özünəinamlı və universal maşınla qısa saç düzümü.'),
    tags: localized(['Минимализм', 'Простой уход'], ['Minimal', 'Easy care'], ['Minimal', 'Asan qulluq']),
    variations: localized(['Классический', 'Фейд', 'Одна длина'], ['Classic', 'Fade', 'Uniform'], ['Klassik', 'Fade', 'Eyni uzunluq']),
    promptHint: 'Buzz cut hairstyle, very short clipper cut, clean and low maintenance, classic barbershop cut',
    previewImage: '/styles/catalog/buzz.jpg', length: 'short', vibe: 'classic',
  },
];

export const STYLE_ID_MAP: Record<string, string> = {
  curlyvolume: 'curly_volume',
  'curly-volume': 'curly_volume',
  curly: 'curly_volume',
  flow_cat: 'flow',
  fringe_cat: 'fringe',
  mod_cat: 'mod',
  warrior_cat: 'warrior',
  buzz_cut: 'buzz',
  flow_cut: 'flow',
  textured_crop: 'crop',
  side_part: 'oxford',
};

export function getHairstyleConfig(id: string): HairstyleConfig | undefined {
  const mappedId = STYLE_ID_MAP[id] || id;
  return HAIRSTYLE_CONFIGS.find((style) => style.id === mappedId);
}

export function getLocalizedHairstyle(style: HairstyleConfig, language: Language): LocalizedHairstyleCopy {
  return {
    name: style.name[language] || style.name.ru,
    description: style.description[language] || style.description.ru,
    tags: style.tags[language] || style.tags.ru,
    variations: style.variations[language] || style.variations.ru,
  };
}

export const QUICK_STYLE_IDS = ['quiff', 'flow', 'fringe', 'gent', 'middle_part', 'mod'];
